import React, { FC, Fragment, useCallback, useEffect, useState } from 'react';
import { Alert, Box, Grid, Typography } from '@mui/material';
import { useLazyQuery } from '@apollo/client';
import { DentistDirectoryFilter } from 'models/client-directory';
import { User, DentistType } from 'models/user';
import { debounce } from 'lodash';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import DentistDirectoryList from 'components/dentist-directory/dentist-directory-list/dentist-directory-list.component';
import Loader from 'components/shared/loader/loader.component';
import DentistDirectoryPageActions from 'components/dentist-directory/dentist-directory-page-actions/dentist-directory-page-actions.component';
import DownloadExportButton from 'components/shared/download-export-button/download-export-button.component';
import BackdropLoader from 'components/shared/backdrop-loader/backdrop-loader.component';
import { useDentistListExport } from 'hooks/useDentistListExport';
import { getLabDentistsSearchTemplate } from 'apollo/templates/user';

const DentistDirectory: FC = () => {
    const { t: translate } = useTranslation(['common', 'client-directory', 'users']);
    // Lab-wide export: every dentist of every customer, independent of the search filters.
    const { requestExport: exportDentistList, isExporting } = useDentistListExport();
    const [labDentists, setLabDentists] = useState<User[]>([] as User[]);
    const filters = JSON.parse(
        localStorage.getItem('dentistListFilter') ||
            JSON.stringify({
                freeText: '',
                connectedType: DentistType.ALL,
                clinic: 'ALL',
            }),
    );
    const [filter, setFilter] = useState<DentistDirectoryFilter>(filters);
    const [showInfo, setShowInfo] = useState<boolean>(false);
    const { showBoundary } = useDenthubErrorBoundary();

    const [getLabDentists, { loading: clientSearchLoading }] = useLazyQuery(getLabDentistsSearchTemplate, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        onError: (err) => {
            showBoundary(err);
        },
        onCompleted: ({ labDentistSearch: searchedDentists }) => {
            setLabDentists(searchedDentists);
        },
    });

    useEffect(() => {
        const initialFilter =
            filter.freeText === '' && filters.connectedType === DentistType.ALL && filters.clinic === 'ALL';
        if (initialFilter) {
            setShowInfo(true);
            setLabDentists([] as User[]);
        } else {
            setShowInfo(false);
            getLabDentists({
                variables: { filters: filter },
            });
        }
    }, [filter]);

    const handleFilterChange = useCallback(
        debounce(({ freeText, connectedType, clinic }: DentistDirectoryFilter) => {
            const listFilters = {
                freeText: freeText,
                connectedType: connectedType,
                clinic: clinic,
            };
            setFilter(listFilters);
            localStorage.setItem('dentistListFilter', JSON.stringify(listFilters));
        }, 400),
        [],
    );

    return (
        <Fragment>
            <Box display="flex">
                <Grid container spacing={1}>
                    <Grid item sm={7} xs={12}>
                        <Typography variant="h1" component="h1">
                            {translate('client-directory:DENTIST_DIRECTORY')}
                        </Typography>
                    </Grid>
                    <Grid item sm={5} xs={12} display="flex" justifyContent="flex-end" alignItems="center">
                        <DownloadExportButton
                            label={translate('users:EXPORT_DENTISTS_EXCEL')}
                            isLoading={isExporting}
                            onDownload={exportDentistList}
                        />
                    </Grid>
                </Grid>
            </Box>
            <BackdropLoader isOpen={isExporting} message={translate('users:CREATING_EXPORT_WAIT')} />
            <DentistDirectoryPageActions dentistListFilter={filter} onFilterChange={handleFilterChange} />
            {showInfo && (
                <Box mt={2}>
                    <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                        {translate('client-directory:SEARCH_DENTIST_INFO')}
                    </Alert>
                </Box>
            )}
            {clientSearchLoading ? (
                <Loader />
            ) : (
                labDentists?.length > 0 && <DentistDirectoryList filteredDentists={labDentists} />
            )}
        </Fragment>
    );
};

export default DentistDirectory;
